"use client"

import Link from "next/link"
import { LogOut, User } from "lucide-react"

import { buttonVariants } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useAuth } from "@/hooks/useAuth"
import { PROFILE_NAV, SITE_NAV } from "@/lib/site-nav"
import { cn } from "@/lib/utils"

function ProfileMenuItems({
  displayName,
  onLogout,
}: {
  displayName: string
  onLogout: () => void
}) {
  return (
    <>
      <DropdownMenuGroup>
        <DropdownMenuLabel className="font-medium break-all whitespace-normal text-foreground">
          {displayName}
        </DropdownMenuLabel>
        <DropdownMenuItem render={<Link href={SITE_NAV.profile.href} prefetch={false} />}>
          Обзор
        </DropdownMenuItem>
        {PROFILE_NAV.map((item) => (
          <DropdownMenuItem
            key={item.href}
            render={<Link href={item.href} prefetch={false} />}
          >
            {item.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuGroup>
      <DropdownMenuSeparator />
      <div className="mx-1 rounded-lg border border-border bg-muted/40 px-2.5 py-2">
        <p className="text-xs text-muted-foreground">Текущий тариф</p>
        <p className="mt-0.5 text-sm font-medium">Бесплатный</p>
      </div>
      <DropdownMenuSeparator />
      <DropdownMenuItem onClick={onLogout}>
        <LogOut className="h-4 w-4 shrink-0" />
        Выйти
      </DropdownMenuItem>
    </>
  )
}

export function SiteAuthControls({
  mobile = false,
  compact = false,
}: {
  mobile?: boolean
  compact?: boolean
}) {
  const { user, isAuthenticated, isLoading, logout } = useAuth()

  // Пока сессия поднимается — не даём ссылку «Войти»: иначе proxy/soft-nav
  // при живом auth_session уводил с /login и форма «не открывалась».
  if (isLoading) {
    if (mobile) {
      return (
        <DropdownMenuItem disabled className="pointer-events-none opacity-50">
          <User className="h-4 w-4 shrink-0" />
          Загрузка…
        </DropdownMenuItem>
      )
    }
    return (
      <span
        className={cn(
          buttonVariants({ variant: "ghost", size: "icon-sm" }),
          "pointer-events-none opacity-50",
          !compact && "hidden md:inline-flex",
        )}
        aria-hidden
      >
        <User className="h-4 w-4 shrink-0" />
      </span>
    )
  }

  if (!isAuthenticated || !user) {
    if (mobile) {
      return (
        <DropdownMenuItem render={<Link href={SITE_NAV.login.href} prefetch={false} />}>
          {SITE_NAV.login.label}
        </DropdownMenuItem>
      )
    }

    if (compact) {
      return (
        <Link
          href={SITE_NAV.login.href}
          prefetch={false}
          className={buttonVariants({ variant: "ghost", size: "icon-sm" })}
          title={SITE_NAV.login.label}
        >
          <User className="h-4 w-4 shrink-0" />
        </Link>
      )
    }

    return (
      <Link
        href={SITE_NAV.login.href}
        prefetch={false}
        className={buttonVariants({
          variant: "default",
          size: "sm",
          className: "hidden gap-1.5 whitespace-nowrap md:inline-flex",
        })}
      >
        {SITE_NAV.login.label}
      </Link>
    )
  }

  const displayName = user.email?.trim() || user.fullname?.trim() || "Профиль"
  const handleLogout = () => void logout()

  if (mobile) {
    return <ProfileMenuItems displayName={displayName} onLogout={handleLogout} />
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={buttonVariants({
          variant: compact ? "ghost" : "outline",
          size: "icon-sm",
          className: compact ? undefined : "hidden md:inline-flex",
        })}
        title={displayName}
        aria-label={SITE_NAV.profile.label}
      >
        <User className="h-4 w-4 shrink-0" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-max min-w-56 max-w-xs">
        <ProfileMenuItems displayName={displayName} onLogout={handleLogout} />
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
